import { ReactNode, createContext, useCallback, useEffect, useRef, useState } from 'react';
import { isFuture, isPast, parseISO } from 'date-fns';
import useAuth from 'hooks/useAuth';
import useProject from 'hooks/useProject';
import { Socket, io } from 'socket.io-client';
import { WEBSOCKET_URL } from '../constants';

export type SocketContextType = {
  socketInstance: Socket | null;
  joinUnitRooms: (roomIds: string[]) => void;
  leaveUnitRooms: (roomIds: string[]) => void;
  leaveAllUnitRooms: () => void;
  disconnect: () => void;
  removeListeners: () => void;
  setTimeSeriesListener: <T>(unitId: string, cb: (data: T) => void) => void;
  setStateListener: <T>(unitId: string, cb: (data: T) => void) => void;
  setEventsListener: (unitId: string, cb: (data: string) => void) => void;
  setComponentListener: <T extends { component?: string }>(
    unitId: string,
    component: string,
    cb: (data: T) => void
  ) => void;
};

export const SocketContext = createContext<SocketContextType | undefined>(undefined);

const RECONNECT_DELAY_MS = 5000;

/**
 * Determines whether a connection to the provided project rooms should be stablished. If there's no project available,
 * connection is allowed because it's supposed to be outside the context of a project, where the user should have
 * authorisation. If there's a project, it shouldn't be a past or future one.
 **/
const shouldJoinProjectRooms = (project) => {
  if (!project) {
    return true;
  }

  if ((project.tsEnd && isPast(parseISO(project.tsEnd))) || isFuture(parseISO(project.tsStart))) {
    return false;
  }

  return true;
};

export const SocketProvider = ({ children }: { children: ReactNode }) => {
  const { getTokenSilently, user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const { project } = useProject();

  const subscribedRoomsRef = useRef<string[]>([]);

  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isConnectedRef = useRef(false);
  const isConnectingRef = useRef(false);
  const establishSocketRef = useRef<() => void>(() => {});
  const currentSocketRef = useRef<Socket | null>(null);
  const isIntentionallyDisconnectedRef = useRef(false);
  const canConnectRef = useRef(false);

  const canConnect = Boolean(user) && shouldJoinProjectRooms(project);
  canConnectRef.current = canConnect;

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    clearReconnectTimeout();
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;

      // The user may have logged out or left the project while this timeout was pending.
      if (canConnectRef.current) {
        establishSocketRef.current();
      }
    }, RECONNECT_DELAY_MS);
  }, [clearReconnectTimeout]);

  const establishSocket = useCallback(() => {
    isIntentionallyDisconnectedRef.current = false;
    isConnectingRef.current = true;

    const newSocket = io(WEBSOCKET_URL, {
      path: '/socket/socket.io',
      // Resolved per attempt so socket.io's own retries never reuse a token that expired while asleep.
      auth: (callback: (data: Record<string, unknown>) => void) => {
        getTokenSilently()
          // An empty payload lets the backend reject the handshake and surface it as a connect_error.
          .then((accessToken: string) => callback({ accessToken }))
          .catch(() => callback({}));
      },
      reconnection: true,
      reconnectionAttempts: 5, // Maximum number of reconnection attempts
      reconnectionDelay: 2000, // Initial delay of reconnection (ms)
      ackTimeout: 10000,
      transports: ['websocket', 'polling'], // use WebSocket first
    });

    currentSocketRef.current = newSocket;
    setSocket(newSocket);

    // A replaced socket keeps emitting while it tears down, and those events describe a connection
    // that no longer exists - letting them through would report the live one as dead.
    const isCurrent = () => currentSocketRef.current === newSocket;

    newSocket.on('connect', () => {
      if (!isCurrent()) {
        return;
      }

      clearReconnectTimeout();
      isConnectedRef.current = true;
      isConnectingRef.current = false;

      if (subscribedRoomsRef.current.length > 0) {
        newSocket.emit('join_units', { rooms: subscribedRoomsRef.current });
      }
    });

    newSocket.on('disconnect', (reason) => {
      if (!isCurrent()) {
        return;
      }

      isConnectedRef.current = false;
      isConnectingRef.current = false;

      // socket.io retries every reason but a server-closed session, and our own disconnect needs none.
      if (reason === 'io server disconnect') {
        scheduleReconnect();
      }
    });

    newSocket.on('connect_error', () => {
      if (!isCurrent()) {
        return;
      }

      isConnectedRef.current = false;
      isConnectingRef.current = false;
    });

    // socket.io goes quiet once it has burned through reconnectionAttempts, which is how a
    // sleeping machine ends up holding a socket that is dead without ever having said so.
    newSocket.io.on('reconnect_failed', () => {
      if (!isCurrent()) {
        return;
      }

      isConnectingRef.current = false;
      scheduleReconnect();
    });
  }, [clearReconnectTimeout, scheduleReconnect, getTokenSilently]);

  useEffect(() => {
    establishSocketRef.current = establishSocket;
  }, [establishSocket]);

  useEffect(() => {
    if (canConnect && !socket) {
      establishSocket();
    }
  }, [canConnect, socket, establishSocket]);

  // The access token expires while the machine sleeps, so every retry socket.io made in the
  // meantime failed on it. Rebuild with a fresh one as soon as the tab or the network is back.
  useEffect(() => {
    if (!canConnect) {
      return undefined;
    }

    const recoverIfNeeded = () => {
      if (isIntentionallyDisconnectedRef.current) {
        return;
      }

      if (document.visibilityState === 'visible' && !isConnectedRef.current && !isConnectingRef.current) {
        clearReconnectTimeout();
        establishSocketRef.current();
      }
    };

    document.addEventListener('visibilitychange', recoverIfNeeded);
    window.addEventListener('online', recoverIfNeeded);

    return () => {
      document.removeEventListener('visibilitychange', recoverIfNeeded);
      window.removeEventListener('online', recoverIfNeeded);
    };
  }, [canConnect, clearReconnectTimeout]);

  useEffect(() => clearReconnectTimeout, [clearReconnectTimeout]);

  const joinUnitRooms = useCallback(
    (roomIds: string[]) => {
      subscribedRoomsRef.current = Array.from(new Set([...subscribedRoomsRef.current, ...roomIds]));
      socket?.emit('join_units', { rooms: roomIds });
    },
    [socket]
  );

  const leaveUnitRooms = useCallback(
    (roomIds: string[]) => {
      subscribedRoomsRef.current = subscribedRoomsRef.current.filter((room) => !roomIds.includes(room));
      socket?.emit('leave_units', { rooms: roomIds });
    },
    [socket]
  );

  const leaveAllUnitRooms = useCallback(() => {
    subscribedRoomsRef.current = [];
    socket?.emit('leave_units', { all: true });
  }, [socket]);

  // The recovery paths above must not undo a disconnect the app asked for.
  const disconnect = useCallback(() => {
    isIntentionallyDisconnectedRef.current = true;
    clearReconnectTimeout();
    socket?.disconnect();
  }, [socket, clearReconnectTimeout]);

  //Listeners

  const setEventsListener = useCallback(
    (unitId: string, cb: (data: string) => void) => {
      const eventName = `${unitId}/events`;
      socket?.on(eventName, (data: string) => cb(data));
    },
    [socket]
  );

  const setTimeSeriesListener = <T,>(unitId: string, cb: (data: T) => void) => {
    const eventName = `${unitId}/ts`;
    socket?.on(eventName, (data: T) => cb(data));
  };

  const setStateListener = <T,>(unitId: string, cb: (data: T) => void) => {
    const eventName = `${unitId}/state`;
    socket?.on(eventName, (data: T) => cb(data));
  };

  const setComponentListener = <T extends { component?: string }>(
    unitId: string,
    component: string,
    cb: (data: T) => void
  ) => {
    socket?.on(unitId, (data: T) => {
      if (data.component === component) {
        cb(data);
      }
    });
  };

  const removeListeners = () => {
    socket?.removeAllListeners();
  };

  useEffect(() => {
    // Socket will be disconnected when component unmounts
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [socket]);

  const contextValue: SocketContextType = {
    socketInstance: socket,
    joinUnitRooms,
    leaveUnitRooms,
    leaveAllUnitRooms,
    disconnect,
    removeListeners,
    setTimeSeriesListener,
    setStateListener,
    setEventsListener,
    setComponentListener,
  };

  return <SocketContext.Provider value={contextValue}>{children}</SocketContext.Provider>;
};
